import { useState } from "react";
import type { ScoredRelease } from "@trackmark/core";
import styles from "./TrackListPull.module.css";

/**
 * `Pull full track list` — the action on a CLAIMED album that turns it into a TRACKED one.
 *
 * The claim was made at import: "yes, I own Definitely Maybe". This is where the app goes and
 * gets the record's actual contents, and it states the count before it acts — the same voice as
 * the add-preview and the import summary.
 */

export interface ReleaseOption extends ScoredRelease {
  /** Set on an unusable release, in the interface's own words. */
  note: string | null;
}

export interface PullPreview {
  adds: number;
  matches: number;
  movedToBonus: number;
  ratedMovedToBonus: number;
  coreCount: number;
  orphans: { id: number; title: string; rating: number | null }[];
}

export interface TrackListPullProps {
  albumId: number;
  ownedCount: number;
  loadReleases: (albumId: number) => Promise<{ releases: ReleaseOption[]; needsChoice: boolean }>;
  loadPreview: (albumId: number, releaseMbid: string) => Promise<PullPreview>;
  onApply: (albumId: number, releaseMbid: string) => Promise<void>;
}

const n = (value: number) => value.toLocaleString();

export function TrackListPull({ albumId, ownedCount, loadReleases, loadPreview, onApply }: TrackListPullProps) {
  const [releases, setReleases] = useState<ReleaseOption[] | null>(null);
  const [chosen, setChosen] = useState<string | null>(null);
  const [preview, setPreview] = useState<PullPreview | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const open = async () => {
    setBusy(true);
    setError(null);
    try {
      const result = await loadReleases(albumId);
      setReleases(result.releases);
      // Auto-select the winner only when it IS one. Where the top two are close the user picks,
      // because this file is full of originals and remasters and guessing gets the year wrong.
      if (!result.needsChoice) {
        const best = result.releases.find((r) => r.usable);
        if (best) await choose(best.mbid);
      }
    } catch (cause) {
      setError(message(cause));
    } finally {
      setBusy(false);
    }
  };

  const choose = async (releaseMbid: string) => {
    setChosen(releaseMbid);
    setPreview(null);
    setError(null);
    setBusy(true);
    try {
      setPreview(await loadPreview(albumId, releaseMbid));
    } catch (cause) {
      setError(message(cause));
      setChosen(null);
    } finally {
      setBusy(false);
    }
  };

  const apply = async () => {
    if (!chosen) return;
    setBusy(true);
    try {
      await onApply(albumId, chosen);
    } catch (cause) {
      setError(message(cause));
      setBusy(false);
    }
  };

  if (releases === null) {
    return (
      <div className={styles.claim} data-testid="pull-prompt">
        <div className={styles.copy}>
          You marked this as a record you own, but the app only has{" "}
          <b>
            {n(ownedCount)} of its {ownedCount === 1 ? "song" : "songs"}
          </b>
          . It gets no album score until the full track list is here.
        </div>
        <button type="button" className={styles.primary} disabled={busy} data-testid="pull-start" onClick={() => void open()}>
          {busy ? "Looking…" : "Pull full track list"}
        </button>
        {error && (
          <div className={styles.error} data-testid="pull-error">
            {error}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className={styles.claim} data-testid="pull-panel">
      <div className={styles.eyebrow}>WHICH RELEASE?</div>

      <div className={styles.releases} data-testid="release-options">
        {releases.slice(0, 6).map((release) => (
          <button
            key={release.mbid}
            type="button"
            className={styles.release}
            data-active={chosen === release.mbid || undefined}
            data-usable={release.usable}
            data-testid="release-option"
            disabled={!release.usable}
            onClick={() => void choose(release.mbid)}
          >
            <span className={styles.releaseTitle}>{release.title}</span>
            <span className={styles.releaseMeta}>
              {[release.date?.slice(0, 4), release.country, release.formats[0]].filter(Boolean).join(" · ")}
            </span>
            <span className={styles.releaseTracks}>{release.trackCount} TRACKS</span>
            {/* The reasons are the app showing its working, so a wrong pick is the user's to
                correct rather than a mystery. */}
            <span className={styles.releaseWhy}>{release.note ?? release.reasons.join(" · ")}</span>
          </button>
        ))}
      </div>

      {preview && (
        <div className={styles.preview} data-testid="pull-preview">
          <div className={styles.copy}>
            Adds <b>{n(preview.adds)}</b> {preview.adds === 1 ? "song" : "songs"}, keeps the{" "}
            <b>{n(preview.matches)}</b> already here, and makes this a{" "}
            <b>{n(preview.coreCount)}-track</b> record.
          </div>

          {preview.movedToBonus > 0 && (
            <div className={styles.orphans} data-testid="pull-orphans">
              <div className={styles.orphanLead}>
                {n(preview.movedToBonus)} {preview.movedToBonus === 1 ? "song is" : "songs are"} not on this release
                and {preview.movedToBonus === 1 ? "moves" : "move"} to bonus.{" "}
                {preview.ratedMovedToBonus > 0 && <b>Their ratings are kept.</b>}
              </div>
              {preview.orphans.map((orphan) => (
                <div key={orphan.id} className={styles.orphan}>
                  <span className={styles.orphanTitle}>{orphan.title}</span>
                  <span className={styles.orphanRating}>{orphan.rating ?? "—"}</span>
                </div>
              ))}
            </div>
          )}

          <button type="button" className={styles.primary} disabled={busy} data-testid="pull-apply" onClick={() => void apply()}>
            {busy ? "Pulling…" : `Add ${n(preview.adds)} and mark as owned`}
          </button>
        </div>
      )}

      {error && (
        <div className={styles.error} data-testid="pull-error">
          {error}
        </div>
      )}
    </div>
  );
}

const message = (cause: unknown) => (cause instanceof Error ? cause.message : "MUSICBRAINZ DIDN'T RESPOND. THE SEARCH WILL RETRY IN A MOMENT.");
